/**
 * Icons — central wrapper around lucide-react.
 *
 * Why a wrapper instead of importing directly everywhere:
 *   1. Single point to swap icon libraries if needed (e.g. Tabler).
 *   2. Consistent default sizing — every Icon is 18×18 unless overridden,
 *      matching the prototype's nav-item .ico (18px).
 *   3. Friendlier names mapped to Lucide equivalents so reading the
 *      prototype's I.dash, I.sheet etc. stays familiar.
 *
 * Verified against lucide-react@0.451.x (pinned in package.json).
 * If an icon is missing after a version bump, find the closest name at
 * https://lucide.dev/icons
 */

import {
  // navigation
  LayoutGrid,
  ClipboardList,
  Users,
  Calendar,
  BarChart3,
  MessageCircleQuestion,
  ShieldCheck,

  // topbar
  Bell,
  Search,
  Settings,
  ChevronRight,
  ChevronDown,

  // task page / general
  ArrowLeft,
  Plus,
  Send,
  Clock,
  Flag,
  CheckCircle2,
  Circle,
  AlertCircle,
  RotateCw,

  // misc
  MoreHorizontal,
  Filter,
  LogOut,
  Eye,
  EyeOff,
  X,
  Pencil,
  Trash2,
  Paperclip,
  ChevronUp,
  BookOpen,
  PanelLeft,
  MessageSquarePlus,
} from 'lucide-react';

/**
 * Friendly aliases — extend as needed.
 * Keys mirror the prototype's I.<name> for easy cross-referencing.
 */
const Icons = {
  // Sidebar nav
  dash: LayoutGrid,
  sheet: ClipboardList,
  team: Users,
  cal: Calendar,
  reports: BarChart3,
  doubts: MessageCircleQuestion,
  approvals: ShieldCheck,

  // Topbar
  bell: Bell,
  search: Search,
  settings: Settings,
  chev: ChevronRight,
  chevDown: ChevronDown,
  chevUp: ChevronUp,

  // Task / common
  back: ArrowLeft,
  plus: Plus,
  send: Send,
  clock: Clock,
  flag: Flag,
  done: CheckCircle2,
  todo: Circle,
  overdue: AlertCircle,
  redo: RotateCw,

  // Misc
  more: MoreHorizontal,
  filter: Filter,
  logout: LogOut,
  eye: Eye,
  eyeOff: EyeOff,
  close: X,
  edit: Pencil,
  trash: Trash2,
  attach: Paperclip,
  book: BookOpen,
  panel: PanelLeft,
  remark: MessageSquarePlus,
};

/**
 * Icon — generic wrapper. Use `<Icon name="dash" />` to render any
 * mapped icon at the default 18px size.
 *
 * Props:
 *   name      — key from the Icons map (required)
 *   size      — number, defaults to 18
 *   className — extra Tailwind classes
 *   ...rest   — forwarded to the underlying Lucide component
 */
export default function Icon({ name, size = 18, className = '', ...rest }) {
  const Cmp = Icons[name];
  if (!Cmp) {
    // Don't crash the UI; log once and render a placeholder.
    if (typeof console !== 'undefined') {
      console.warn(`[ORIN] Unknown icon name: "${name}"`);
    }
    return (
      <span
        aria-hidden="true"
        style={{ width: size, height: size, display: 'inline-block' }}
      />
    );
  }
  return <Cmp size={size} className={className} aria-hidden="true" {...rest} />;
}